import {
  ClaimStatusEnum,
  ServiceTypeEnum,
  TitleTypeEnum,
} from '@sr-sdks/api-sdk-axios';
import type { SingleOption } from '@sr/ui-library';
import { SERVICE_BADGE_PRESETS } from '../../../components/Badges/appBadgeConstants';
import type { ServiceBadgeType } from '../../../components/Badges/appBadgeTypes';
import { titleTypeLabel } from '../../../components/TitleCard/titleMeta';

/**
 * Option lists for the Titles L1 filter bar. Kept free of React so the parsing
 * guards in `useTitleFilters` and the controls in `TitlesFilterBar` can share
 * one source of truth.
 */

/**
 * The option shape `Combobox` already takes, narrowed to a required `label`:
 * every filter option has one, and the client list sorts on it. Type-only
 * import, so this file stays free of the React the library ships.
 */
export type FilterOption = SingleOption & { label: string };

// ===== Service =====

/** Titles cannot be scoped to the `'*'` ("all services") service type. */
export const SERVICE_OPTIONS: FilterOption[] = Object.entries(
  SERVICE_BADGE_PRESETS,
)
  .filter(([value]) => value !== ServiceTypeEnum.Star)
  .map(([value, meta]) => ({ label: String(meta.children), value }));

const SERVICE_VALUES = new Set(SERVICE_OPTIONS.map((option) => option.value));

export const isServiceValue = (value: string): value is ServiceBadgeType =>
  SERVICE_VALUES.has(value);

// ===== Claim status =====

/**
 * Derived from the generated `ClaimStatusEnum`, which the titles backend defines
 * from MSR2's `ClaimStatuses`. Values are the strings actually stored on the
 * title, and they are already display-ready, so label and value are the same.
 */
export const CLAIM_STATUS_OPTIONS: FilterOption[] = Object.values(
  ClaimStatusEnum,
).map((status) => ({ label: status, value: status }));

const CLAIM_STATUS_VALUES = new Set<string>(Object.values(ClaimStatusEnum));

export const isClaimStatusValue = (value: string): value is ClaimStatusEnum =>
  CLAIM_STATUS_VALUES.has(value);

// ===== Program type =====

/** An option can cover several enum members, e.g. "Series". */
type ProgramTypeOption = FilterOption & { values: TitleTypeEnum[] };

/**
 * Enum members that share one option, keyed by the slug covering them. Only
 * needed where `titleTypeLabel` gives two members the same label: listing
 * "Series" twice would be meaningless to the user.
 */
const PROGRAM_TYPE_GROUPS: Record<string, TitleTypeEnum[]> = {
  series: [TitleTypeEnum.SeriesFactual, TitleTypeEnum.SeriesFictional],
};

/**
 * One option per slug: the enum value itself, or the group slug for members that
 * share a label. Slugs come from the enum rather than the display label so that
 * renaming a label cannot change the URL vocabulary and silently break links
 * already shared — the guard drops unrecognised values quietly.
 *
 * Derived rather than hand-listed: `titleTypeLabel` is typed
 * `Record<TitleTypeEnum, string>`, so a new enum member must be labelled there
 * and then shows up here on its own.
 */
const buildProgramTypeOptions = () => {
  const slugByTitleType = new Map<TitleTypeEnum, string>();

  for (const [slug, titleTypes] of Object.entries(PROGRAM_TYPE_GROUPS))
    for (const titleType of titleTypes) slugByTitleType.set(titleType, slug);

  const byValue = new Map<string, ProgramTypeOption>();

  for (const titleType of Object.values(TitleTypeEnum)) {
    const value = slugByTitleType.get(titleType) ?? titleType;
    const option = byValue.get(value);

    if (option) option.values.push(titleType);
    else
      byValue.set(value, {
        label: titleTypeLabel[titleType],
        value,
        values: [titleType],
      });
  }

  return [...byValue.values()];
};

export const PROGRAM_TYPE_OPTIONS = buildProgramTypeOptions();

const PROGRAM_TYPE_BY_VALUE = new Map(
  PROGRAM_TYPE_OPTIONS.map((option) => [option.value, option]),
);

/**
 * Slugs are built at runtime from the enum and its groups, so there is no union
 * to narrow to — unlike service and claim status, this guard only asserts
 * `string`. Kept a predicate so `useFilterParam` types every filter the same way.
 */
export const isProgramTypeValue = (value: string): value is string =>
  PROGRAM_TYPE_BY_VALUE.has(value);

/** Expands selected option slugs to the `titleType` values sent to the API. */
export const toTitleTypes = (values: string[]) =>
  values.flatMap((value) => PROGRAM_TYPE_BY_VALUE.get(value)?.values ?? []);

// ===== Production year =====

/**
 * Digits in a production year, mirroring `@Matches(/^\d{4}$/)` on the DTO's
 * `year`. The single source for the rule on this side of the wire: the guard
 * below builds its pattern from it, and `TitlesFilterBar` bounds the input with
 * it, so the two cannot disagree.
 */
export const YEAR_LENGTH = 4;

const PRODUCTION_YEAR_PATTERN = new RegExp(`^\\d{${YEAR_LENGTH}}$`);

/**
 * Production year is typed rather than picked from a list: the catalogue reaches
 * back further than any sensible dropdown, and there is no distinct-values
 * source to build one from.
 *
 * The shape check is not cosmetic. A GET 400 makes `axiosInstance` swap the whole
 * `<Outlet>` for the error page — filter bar included — so a half-typed year must
 * never reach the wire.
 */
export const isProductionYearValue = (value: string) =>
  PRODUCTION_YEAR_PATTERN.test(value);

// ===== Client =====

/**
 * Client ids are uuids; anything else in the URL is discarded before it is sent.
 * A shape check, so like the program type guard it only asserts `string`.
 */
export const isClientIdValue = (value: string): value is string =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

/** Matches `@ArrayMaxSize(100)` on the Titles and Tasks list DTOs' array params. */
export const MAX_LIST_VALUES = 100;
